/// <reference types="google.maps" />
// `@vis.gl/react-google-maps` types its props over the global `google.maps`
// namespace and does not reference it. Without this reference the map options
// resolve to nothing, and their optional props turn required.

'use client';

import { type ComponentProps, useRef } from 'react';
import { AdvancedMarker, APIProvider, Map, Pin } from '@vis.gl/react-google-maps';
import { useNearViewport } from './util/nearViewport';

/** How far outside the viewport the frame starts to load. */
const LOAD_MARGIN = '200px';

export type GoogleMapProps = Pick<ComponentProps<typeof Map>, 'mapId' | 'gestureHandling'> & {
  readonly apiKey: ComponentProps<typeof APIProvider>['apiKey'];
  readonly latitude: number;
  readonly longitude: number;
  readonly zoom: NonNullable<ComponentProps<typeof Map>['defaultZoom']>;
};

/**
 * Changed coordinates or zoom reset the camera. Equal values preserve shopper movement.
 *
 * Google bills each load of the Maps JavaScript API script. The frame stays
 * empty until it comes near the viewport. Only then does `APIProvider` mount
 * and load the script.
 */
export const GoogleMap = ({ apiKey, latitude, longitude, zoom, ...options }: GoogleMapProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const near = useNearViewport(frameRef, LOAD_MARGIN);
  const center = { lat: latitude, lng: longitude };

  return (
    <div ref={frameRef} data-slot="google-map" className="size-full">
      {near ? (
        <APIProvider apiKey={apiKey}>
          <Map
            key={`${latitude}:${longitude}:${zoom}`}
            {...options}
            defaultCenter={center}
            defaultZoom={zoom}
          >
            <AdvancedMarker position={center}>
              <Pin background="#EA4335" glyphColor="#AF3228" borderColor="#AF3228" />
            </AdvancedMarker>
          </Map>
        </APIProvider>
      ) : null}
    </div>
  );
};
